import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { Building2, ListChecks, Users, Sparkles, FileDown, PlayCircle, ArrowRight } from "lucide-react";

const STEPS = [
  { k: "s1", icon: Building2 },
  { k: "s2", icon: ListChecks },
  { k: "s3", icon: Users },
  { k: "s4", icon: Sparkles },
  { k: "s5", icon: FileDown },
];

// Owner can paste a YouTube/Vimeo/MP4 link in /settings; until then the
// animated explainer below stands in for it.
function embedUrl(raw) {
  const url = (raw || "").trim();
  if (!/^https?:\/\/\S+$/i.test(url)) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) return { type: "video", src: url };
  return { type: "iframe", src: url };
}

export function HowItWorks({ open, onOpenChange, onStart, brandColor = "#6366F1", videoUrl = "" }) {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  const video = embedUrl(videoUrl);

  useEffect(() => {
    if (!open || video) return;
    const id = setInterval(() => setActive((p) => (p + 1) % STEPS.length), 3200);
    return () => clearInterval(id);
  }, [open, video]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#DCE4EF] border-slate-200 text-slate-900 max-w-2xl max-h-[88vh] overflow-y-auto"
        data-testid="how-it-works-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl sm:text-2xl">{t("hiw.title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("hiw.subtitle")}</DialogDescription>
        </DialogHeader>

        {video ? (
          <div
            className="relative w-full h-[200px] sm:h-[300px] rounded-xl overflow-hidden border border-slate-200 bg-black"
            data-testid="hiw-video"
          >
            {video.type === "video" ? (
              <video src={video.src} controls className="absolute inset-0 w-full h-full" />
            ) : (
              <iframe
                title="intro-video"
                src={video.src}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        ) : (
          <div
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-2.5 text-xs text-slate-500"
            data-testid="hiw-video-slot"
          >
            <PlayCircle size={16} style={{ color: brandColor }} />
            {t("hiw.videoSoon")}
          </div>
        )}

        <ol className="space-y-2.5 mt-2" data-testid="hiw-steps">
          {STEPS.map((s, i) => {
            const isActive = !video && i === active;
            return (
              <motion.li
                key={s.k}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.07 }}
                onMouseEnter={() => setActive(i)}
                data-testid={`hiw-step-${i + 1}`}
                className={`flex gap-3.5 rounded-xl border p-3.5 transition-colors duration-300 ${
                  isActive ? "border-slate-400 bg-slate-100" : "border-slate-200 bg-white"
                }`}
              >
                <span
                  className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-300"
                  style={{
                    backgroundColor: isActive ? brandColor : `${brandColor}1a`,
                    transform: isActive ? "scale(1.06)" : "none",
                  }}
                >
                  <s.icon size={18} style={{ color: isActive ? "#fff" : brandColor }} />
                  <span className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-slate-300 text-[10px] font-mono font-bold flex items-center justify-center text-slate-700">
                    {i + 1}
                  </span>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{t(`hiw.${s.k}.title`)}</p>
                  <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{t(`hiw.${s.k}.desc`)}</p>
                </div>
              </motion.li>
            );
          })}
        </ol>

        <Button
          onClick={() => {
            onOpenChange(false);
            onStart();
          }}
          style={{ backgroundColor: brandColor }}
          className="w-full text-white hover:opacity-90 h-11 mt-1"
          data-testid="hiw-start-btn"
        >
          {t("hiw.start")} <ArrowRight size={17} className="ml-2" />
        </Button>
      </DialogContent>
    </Dialog>
  );
}
